import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateTimeBoth } from '../utils/dates';

const TYPE_LABELS = {
  task_assigned: 'إسناد مهمة',
  task_updated: 'تحديث مهمة',
  task_event: 'حدث على مهمة',
  approval_needed: 'بانتظار اعتماد',
  overdue: 'متأخر',
  broadcast: 'رسالة عامة',
  client_request: 'طلب عميل',
  request_update: 'تحديث تذكرة',
  contract_reminder: 'تذكير عقد',
  book_summary: 'تلخيص كتاب',
  mcp_approval: 'موافقة للموصل',
  system: 'النظام',
};

const TYPE_BADGE = {
  task_assigned: 'in_progress',
  task_updated: 'in_progress',
  task_event: 'in_progress',
  approval_needed: 'waiting_approval',
  overdue: 'revision',
  broadcast: 'gold',
  client_request: 'blue',
  request_update: 'blue',
  contract_reminder: 'warning',
  book_summary: 'approved',
  system: 'neutral',
};

const TYPE_ICON = {
  task_assigned: '📌',
  task_updated: '✏️',
  task_event: '✏️',
  approval_needed: '🔎',
  overdue: '⏰',
  broadcast: '📢',
  client_request: '📥',
  request_update: '💬',
  contract_reminder: '📄',
  book_summary: '📚',
  system: '⚙️',
};

export default function Notifications() {
  const { api } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ unread: false, type: '' });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p });
      if (filters.unread) params.append('unread', '1');
      if (filters.type) params.append('type', filters.type);
      const res = await api(`/notifications?${params.toString()}`);
      setItems(res.items || []);
      setTypes(res.types || []);
      setUnread(res.unread || 0);
      setTotal(res.total || 0);
      setPage(res.page || 1);
      setLastPage(res.last_page || 1);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
    // toast خارج التبعيات عمداً: مزوّده يعيد بناء الكائن عند كل رسالة، فإدراجه
    // يعيد جلب القائمة مع كل toast
  }, [api, filters]); // eslint-disable-line

  useEffect(() => { load(1); }, [load]);

  const setRead = async (n, read) => {
    // تفاؤلي: العدّاد أعلى الشاشة هو ما ينتظره المستخدم، والخطأ يتراجع عن
    // هذا العنصر وحده — إعادة لقطة القائمة كلّها تمحو ما تغيّر بعدها.
    setItems(list => list.map(x => (x.id === n.id ? { ...x, is_read: read } : x)));
    setUnread(c => Math.max(0, c + (read ? -1 : 1)));
    try {
      const res = await api(`/notifications/${n.id}/${read ? 'read' : 'unread'}`, { method: 'PUT' });
      if (typeof res.unread === 'number') setUnread(res.unread);
    } catch (err) {
      setItems(list => list.map(x => (x.id === n.id ? { ...x, is_read: !read } : x)));
      setUnread(c => Math.max(0, c + (read ? 1 : -1)));
      toast.error(err.message);
    }
  };

  const open = (n) => {
    if (!n.is_read) setRead(n, true);
    const to = n.link || (n.related_task_id ? `/tasks/${n.related_task_id}` : null);
    if (to) navigate(to);
  };

  const markAll = async () => {
    try {
      const res = await api('/notifications/read-all', { method: 'PUT' });
      toast.success(res.marked ? `عُلّم ${res.marked} إشعاراً مقروءاً` : 'لا توجد إشعارات غير مقروءة');
      load(page);
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>
          التنبيهات{' '}
          {total > 0 && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>
              ({total}{unread > 0 ? ` — ${unread} غير مقروء` : ''})
            </span>
          )}
        </h2>
        <button className="btn btn-outline btn-sm" onClick={markAll} disabled={unread === 0}>
          تعليم الكل مقروءاً
        </button>
      </div>

      <div className="filters">
        <div className="form-group">
          <label className="form-label">النوع</label>
          <select
            className="form-select"
            value={filters.type}
            onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
          >
            <option value="">كل الأنواع</option>
            {types.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={filters.unread}
              onChange={e => setFilters(f => ({ ...f, unread: e.target.checked }))}
            />
            غير المقروء فقط
          </label>
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setFilters({ unread: false, type: '' })}>
            إعادة ضبط
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loader">جاري التحميل...</div> : items.length === 0 ? (
          <div className="empty">
            <div className="empty-icon">🔔</div>
            {filters.unread || filters.type ? 'لا توجد تنبيهات مطابقة' : 'لا توجد تنبيهات'}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map(n => {
              const to = n.link || (n.related_task_id ? `/tasks/${n.related_task_id}` : null);
              return (
                <li
                  key={n.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '12px 14px',
                    borderBottom: '1px solid var(--border)',
                    background: n.is_read ? 'transparent' : 'var(--c-blue-100)',
                    cursor: to ? 'pointer' : 'default',
                  }}
                  onClick={() => open(n)}
                >
                  <div style={{ fontSize: 20, lineHeight: 1.3 }}>{TYPE_ICON[n.type] || '🔔'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: n.is_read ? 500 : 700 }}>{n.title}</span>
                      <span className={`badge badge-${TYPE_BADGE[n.type] || 'neutral'}`} style={{ fontSize: 11 }}>
                        {TYPE_LABELS[n.type] || n.type}
                      </span>
                    </div>
                    {n.message && (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                        {n.message}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
                      {fmtDateTimeBoth(n.created_at)}
                    </div>
                  </div>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
                    onClick={e => { e.stopPropagation(); setRead(n, !n.is_read); }}
                  >
                    {n.is_read ? 'غير مقروء' : 'مقروء'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {lastPage > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => load(page - 1)} disabled={page <= 1}>السابق</button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>صفحة {page} من {lastPage}</span>
          <button className="btn btn-outline btn-sm" onClick={() => load(page + 1)} disabled={page >= lastPage}>التالي</button>
        </div>
      )}
    </div>
  );
}
